import {
  type BookkeepingUpdatedRealtimeEvent,
  CompleteBookkeepingCycleRequest,
  ConfirmUnmanagedClientAccessRequest,
  ConfirmUnmanagedPlatformAccessRequest,
  CorrectBookkeepingCycleRequest,
  GetBookkeepingProgressionInfoRequest,
  StartNewBookkeepingCycleRequest,
} from '@taxfyle/api-internal/internal/bookkeeping_pb'
import { collection } from '@/infra/collection'
import { type JobsApi } from '@/features/jobs/api/job-api'
import { BookkeepingProgressionInfo } from './BookkeepingProgressionInfo'
import { Id } from '@/infra/id'
import { map, share } from 'rxjs'
import { filterNonNil } from '@/utils/rx-util'
import type { BookkeepingSoftware } from './bookkeeping-software'
import {
  mapBookkeepingProgressionInfoProtoToProps,
  mapBookkeepingSoftwareToProto,
} from './bookkeeping-progression-info-mappers'

interface CompletedBookkeepingItem {
  id: Id
  completedCount: number
}

interface CompleteBookkeepingCycleOptions {
  jobId: Id
  cycleId: Id
  completedItems: CompletedBookkeepingItem[]
}

interface CorrectedBookkeepingItem {
  id: Id
  completedCount: number
}

interface CorrectBookkeepingCycleOptions {
  jobId: Id
  cycleId: Id
  correctedItems: CorrectedBookkeepingItem[]
}

export class BookkeepingProgressionStore {
  readonly bookkeepingProgressions = collection({
    create: () => new BookkeepingProgressionInfo(),
    getModelId: (bpi) => bpi.id,
    getDataId: (props) => props.id,
  })

  constructor(private readonly _api: JobsApi) {
    this._api.realtime$
      .pipe(
        map((evt) => evt.getBookkeepingUpdated()),
        filterNonNil(),
        share(),
      )
      .subscribe(this._onRemoteEvent.bind(this))
  }

  /**
   * Fetches the bookkeeping info for the given job and adds it to the collection if exists.
   * @param jobId
   */
  async fetchBookkeepingProgressionInfo(jobId: Id, signal?: AbortSignal) {
    const request = new GetBookkeepingProgressionInfoRequest().setId(
      Id.assert(jobId),
    )

    const response =
      await this._api.bookkeeping.getBookkeepingProgressionInfo(request)
    signal?.throwIfAborted()

    const info = response.getBookkeepingProgressionInfo()
    if (info) {
      this.bookkeepingProgressions.set(
        mapBookkeepingProgressionInfoProtoToProps(info),
      )
    }
  }

  /**
   * Returns the bookkeeping progression info for the given job id.
   * @param jobId
   * @returns
   */
  getBookkeepingProgressionInfo(jobId: Id) {
    return this.bookkeepingProgressions.get(jobId)
  }

  /**
   * Confirms that the Pro has access to the bookkeeping platform.
   * @param jobId
   */
  async confirmUnmanagedPlatformAccess(jobId: Id) {
    const request = new ConfirmUnmanagedPlatformAccessRequest().setId(
      Id.assert(jobId),
    )

    await this._api.bookkeeping.confirmUnmanagedPlatformAccess(request)

    await this.fetchBookkeepingProgressionInfo(jobId)
  }

  /**
   * Confirms that the client granted the Pro access to their books.
   * @param jobId
   * @param software The software the client uses for their books.
   */
  async confirmUnmanagedClientAccess(jobId: Id, software: BookkeepingSoftware) {
    const request = new ConfirmUnmanagedClientAccessRequest()
      .setId(Id.assert(jobId))
      .setSoftware(mapBookkeepingSoftwareToProto(software))

    await this._api.bookkeeping.confirmUnmanagedClientAccess(request)

    await this.fetchBookkeepingProgressionInfo(jobId)
  }

  /**
   * Starts a new bookkeeping cycle.
   * @param jobId
   */
  async startNewBookkeepingCycle(jobId: Id) {
    const request = new StartNewBookkeepingCycleRequest().setId(
      Id.assert(jobId),
    )

    await this._api.bookkeeping.startNewBookkeepingCycle(request)

    await this.fetchBookkeepingProgressionInfo(jobId)
  }

  /**
   * Completes a bookkeeping cycle.
   * @param options
   */
  async completeBookkeepingCycle(options: CompleteBookkeepingCycleOptions) {
    const request = new CompleteBookkeepingCycleRequest()
      .setId(Id.assert(options.jobId))
      .setCycleId(Id.assert(options.cycleId))
      .setCompletedItemsList(
        options.completedItems.map((ci) =>
          new CompleteBookkeepingCycleRequest.CompletedItem()
            .setId(Id.assert(ci.id))
            .setCompletedCount(ci.completedCount),
        ),
      )

    await this._api.bookkeeping.completeBookkeepingCycle(request)

    await this.fetchBookkeepingProgressionInfo(options.jobId)
  }

  /**
   * Corrects a bookkeeping cycle.
   * @param options
   */
  async correctBookkeepingCycle(options: CorrectBookkeepingCycleOptions) {
    const request = new CorrectBookkeepingCycleRequest()
      .setId(Id.assert(options.jobId))
      .setCycleId(Id.assert(options.cycleId))
      .setCorrectedItemsList(
        options.correctedItems.map((ci) =>
          new CorrectBookkeepingCycleRequest.CorrectedItem()
            .setId(Id.assert(ci.id))
            .setCompletedCount(ci.completedCount),
        ),
      )

    await this._api.bookkeeping.correctBookkeepingCycle(request)

    await this.fetchBookkeepingProgressionInfo(options.jobId)
  }

  /**
   * Handles realtime events.
   */
  private _onRemoteEvent(event: BookkeepingUpdatedRealtimeEvent) {
    const proto = event.getProgressionInfo()
    if (!proto) {
      return
    }

    this.bookkeepingProgressions.set(
      mapBookkeepingProgressionInfoProtoToProps(proto),
    )
  }
}
